import type { Record_Log } from "graphql/generated/graphql";

/**
 * Pure helpers for the partition-pruned gradual record_log read. The gradual
 * endpoint returns one UTC-day window per call (newest-first) plus a keyset
 * cursor; the UI accumulates windows until a target row count is reached or the
 * user stops. These helpers own the accumulation/continuation decisions so they
 * are unit-testable without React. See
 * operator-task-fe-record-logs-tab-default-load-slow-no-partition-bound.
 */

export type GradualLogRow = Record_Log;

/** Keyset cursor sent back to fetch the next (older) window. */
export interface GradualCursorInput {
    created_at: string;
    id?: string;
}

/** One page (window) of the gradual read, as returned under
 *  `data.record_log_list_gradual`. */
export interface GradualLogPage {
    record_log_list?: Array<GradualLogRow | null> | null;
    more_available?: boolean | null;
    scanned_from?: string | null;
    scanned_through?: string | null;
    continue_before?: { created_at?: string | null; id?: string | null } | null;
    /** Forward (newer) read only: the cursor for the next newer page, null once
     *  the newest row has been reached. */
    continue_after?: { created_at?: string | null; id?: string | null } | null;
    /** Count mode only: true when the server-side walk stopped on a safety cap
     *  (iterations / wall-time) rather than reaching `count` or end-of-logs, so
     *  more rows remain and the caller should resume via `continue_before`. */
    capped?: boolean | null;
}

/** Non-null rows of a gradual page, in server order (newest-first). */
export const pageRows = (page?: GradualLogPage | null): GradualLogRow[] =>
    (page?.record_log_list ?? []).filter((row): row is GradualLogRow => Boolean(row));

/**
 * Append newly-fetched rows to the accumulated list, dropping any whose id is
 * already present. A keyset boundary can re-emit the anchor row, so dedup by id
 * keeps the list clean. Order is preserved: existing rows first (newest-first),
 * then previously-unseen incoming rows.
 */
export const mergeGradualRows = (
    existing: GradualLogRow[],
    incoming: GradualLogRow[]
): GradualLogRow[] => {
    const seen = new Set(existing.map((row) => row.id));
    const merged = existing.slice();
    for (const row of incoming) {
        if (row.id && seen.has(row.id)) {
            continue;
        }
        if (row.id) {
            seen.add(row.id);
        }
        merged.push(row);
    }
    return merged;
};

// ---------------------------------------------------------------------------
// Live row streaming (Decision 4 of the Logs-tab redesign — Stefan, 2026-10-03;
// operator-task-fe-logs-tab-live-row-updates).
// ---------------------------------------------------------------------------
// While the live-updates toggle is on, a lightweight subscription pushes the
// newest record_log rows (the `record_log_plain_list` subscription — no COUNT,
// partition-floored to the current UTC day — NOT the classic `record_log_list`
// subscription, whose unconditional full-partition COUNT is exactly what the
// gradual rework removed). Each push is MERGED INTO the accumulated day-stepped
// list rather than replacing it, so a user who has loaded older days keeps them
// while new rows appear at the top.

/** How many newest rows the live subscription requests per push. Small: it only
 *  needs to catch rows created since the last push, and a low limit keeps the
 *  GraphQL query cost (~1×limit for plain scalar rows) far under the 2000 cap. */
export const LIVE_WINDOW_LIMIT = 50;

/**
 * Merge live-pushed newest rows into the accumulated list. Unlike
 * `mergeGradualRows` (which appends OLDER rows at the bottom for "Load older"),
 * live arrivals are NEWER than everything loaded, so the union is re-sorted
 * newest-first and a new row lands at the TOP in the correct place. Rows already
 * present (by id) are dropped. When nothing new arrived the SAME array reference
 * is returned so the caller can skip a needless state update / re-render (and
 * never loops).
 *
 * The row `created_at` the API returns is rounded down to whole seconds, while
 * the server orders by the full-precision timestamp. Sorting on that value plus
 * `id` would reshuffle the rows written within one second (the id is random).
 * So the sort compares `created_at` only and is stable: rows that share a second
 * keep the server order, and new rows go before existing rows of the same second
 * (they are newer). `incoming` must be in server order (newest-first), as every
 * gradual page and live push is.
 */
export const mergeLiveRows = (
    existing: GradualLogRow[],
    incoming: GradualLogRow[]
): GradualLogRow[] => {
    const seen = new Set(existing.map((r) => r.id));
    const fresh = incoming.filter((r) => r.id && !seen.has(r.id));
    if (fresh.length === 0) {
        return existing;
    }
    const merged = fresh.concat(existing);
    merged.sort((a, b) => {
        const ac = a.created_at ?? "";
        const bc = b.created_at ?? "";
        if (ac === bc) {
            return 0;
        }
        return ac < bc ? 1 : -1;
    });
    return merged;
};

/** True when an ISO timestamp carries no fractional seconds — i.e. it is the
 *  API's whole-second rendering of a full-precision server value. */
const isWholeSecond = (iso: string): boolean =>
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(Z|[+-]\d{2}:?\d{2})$/.test(iso);

/** The ISO timestamp one second after `iso`, or null if it cannot be parsed. */
const nextSecond = (iso: string): string | null => {
    const ms = Date.parse(iso);
    if (Number.isNaN(ms)) {
        return null;
    }
    return new Date(ms + 1000).toISOString();
};

/**
 * Cursor for rows OLDER than `row`, built from a loaded row (not a server
 * cursor). The server compares `(created_at, id)` at full precision, but a row's
 * `created_at` arrives rounded down to the second, so `{created_at, id}` of the
 * row would skip the older rows written earlier in that same second. Instead the
 * cursor is the next whole second without an id (`created_at < next second`):
 * it re-reads the rest of that second, and the caller's dedup by id drops the
 * rows already loaded.
 */
export const olderThanRowCursor = (row?: GradualLogRow | null): GradualCursorInput | null => {
    if (!row?.created_at) {
        return null;
    }
    if (isWholeSecond(row.created_at)) {
        const upper = nextSecond(row.created_at);
        if (upper !== null) {
            return { created_at: upper };
        }
    }
    return row.id ? { created_at: row.created_at, id: row.id } : { created_at: row.created_at };
};

/**
 * Cursor for rows NEWER than `row`, built from a loaded row. For the same
 * rounding reason the cursor is the row's whole second without an id, which the
 * server reads as `created_at >= that second`: newer rows from the same second
 * are included, and dedup by id drops the ones already loaded.
 */
export const newerThanRowCursor = (row?: GradualLogRow | null): GradualCursorInput | null => {
    if (!row?.created_at) {
        return null;
    }
    if (isWholeSecond(row.created_at)) {
        return { created_at: row.created_at };
    }
    return row.id ? { created_at: row.created_at, id: row.id } : { created_at: row.created_at };
};

/** The continuation cursor for the next (older) gradual call, or null when the
 *  history is exhausted (no `continue_before.created_at`). */
export const nextCursor = (page?: GradualLogPage | null): GradualCursorInput | null => {
    const cursor = page?.continue_before;
    if (!cursor?.created_at) {
        return null;
    }
    return cursor.id
        ? { created_at: cursor.created_at, id: cursor.id }
        : { created_at: cursor.created_at };
};

/** The cursor for the next FORWARD (newer) gradual call, or null once the newest
 *  row has been reached (no `continue_after.created_at`). */
export const nextNewerCursor = (page?: GradualLogPage | null): GradualCursorInput | null => {
    const cursor = page?.continue_after;
    if (!cursor?.created_at) {
        return null;
    }
    return cursor.id
        ? { created_at: cursor.created_at, id: cursor.id }
        : { created_at: cursor.created_at };
};

/** The keyset cursor of the newest loaded row (index 0 of the newest-first list),
 *  i.e. where a forward catch-up read starts. null when nothing is loaded. */
export const newestRowCursor = (rows: GradualLogRow[]): GradualCursorInput | null =>
    newerThanRowCursor(rows[0]);

/** Upper bound on forward pages one catch-up runs before it gives up and lets the
 *  live subscription take over. Each page is up to a screenful, and the list is
 *  trimmed to the in-memory cap as it goes, so this only guards against a record
 *  that writes logs faster than the catch-up can read them. */
export const MAX_NEWER_CATCHUP_PAGES = 20;

/**
 * Bound the in-memory row list. The DOM is already windowed (only near-viewport
 * rows are mounted — leg B), but the accumulated `rows` array itself grows
 * unbounded: live updates push newer rows at the top and "load older" appends
 * older rows at the bottom, so a long-lived Logs tab keeps every row ever seen in
 * memory and the virtual-list scrollbar handle shrinks to an unusable sliver
 * (Stefan, 2026-10-15). This keeps the NEWEST `maxRows` rows and discards the
 * oldest beyond that — the rows farthest from the top, which is where the user
 * sits while live updates stream. Because the discarded rows are the oldest, the
 * caller resumes loading them by continuing OLDER than the new oldest-retained
 * row, so no gap opens; the returned `olderCursor` is exactly that continuation.
 *
 * Only correct to apply while the user is scrolled to the TOP (newest rows): it
 * retains the newest rows, so trimming while scrolled into older history would
 * discard what the user is looking at. The caller gates it on that (the live
 * subscription is itself top-gated). Trimming the newest rows while scrolled down
 * is deferred until a backend "newer" continuation cursor exists
 * (operator-task-fe-logs-tab-load-newer-to-newest-before-subscribe).
 *
 * Returns the SAME `rows` reference and `trimmed: false` when nothing is dropped
 * (maxRows unset/non-positive, or already within the cap), so the caller can skip
 * a needless state update / re-render. Rows are in newest-first order (index 0 =
 * newest), as produced by mergeGradualRows and mergeLiveRows. See
 * operator-task-fe-logs-tab-bounded-dom-virtualization.
 */
export interface CapResult {
    rows: GradualLogRow[];
    /** Continuation cursor for the next OLDER fetch after a trim: positioned at the
     *  new oldest-retained row so the dropped rows re-load contiguously (dedup by id
     *  drops any re-emitted boundary row). null when nothing was trimmed. */
    olderCursor: GradualCursorInput | null;
    trimmed: boolean;
}

export const capNewestRows = (rows: GradualLogRow[], maxRows: number): CapResult => {
    if (!maxRows || maxRows <= 0 || rows.length <= maxRows) {
        return { rows, olderCursor: null, trimmed: false };
    }
    const kept = rows.slice(0, maxRows);
    const oldest = kept[kept.length - 1];
    const olderCursor = olderThanRowCursor(oldest);
    return { rows: kept, olderCursor, trimmed: true };
};

/**
 * The mirror of capNewestRows for when the user is deep in older history (the
 * live subscription is closed): keep the OLDEST `maxRows` rows, which is where the
 * user is reading, and drop the newest rows beyond the cap from the top. The
 * dropped rows are newer than everything retained, so the forward catch-up on
 * return to the top re-loads them from the new top row via `continue_after` with
 * no gap. Returns the same array (trimmed=false) when nothing is over the cap.
 */
export const capOldestRows = (
    rows: GradualLogRow[],
    maxRows: number
): { rows: GradualLogRow[]; trimmed: boolean } => {
    if (!maxRows || maxRows <= 0 || rows.length <= maxRows) {
        return { rows, trimmed: false };
    }
    return { rows: rows.slice(rows.length - maxRows), trimmed: true };
};

/**
 * The GraphQL query-cost cap (2000) is charged against the `limit` argument (the
 * paginated `record_log_list_gradual` field costs a small multiple of `limit`),
 * so a large per-window `limit` can 400 the query ("Maximum query cost limit of
 * 2000 exceeded", surfacing as "Could not load logs"). In count mode the server
 * walks and keyset-pages the partitions itself, bounded by `count`; `limit` is
 * only the per-window SQL page ceiling. Cap it well under the cost cap: 300 keeps
 * a comfortable margin and is robust to future envelope-field additions. The
 * backend still returns a `continue_before` cursor, so capping drops no rows —
 * the next screenful resumes from the cursor. See
 * operator-task-fe-logs-tab-bounded-dom-virtualization.
 */
export const WINDOW_FETCH_LIMIT_CAP = 300;

/** The per-window SQL page ceiling (`limit`) to send to the gradual endpoint for a
 *  given rows-per-page: the page size, capped at WINDOW_FETCH_LIMIT_CAP so the
 *  GraphQL query cost stays under the 2000 cap. The server-side count walk still
 *  reaches the requested `count` across keyset round-trips. Floored at 1 so a
 *  degenerate 0/NaN page size never sends limit 0. */
export const windowFetchLimit = (
    pageSize: number,
    cap: number = WINDOW_FETCH_LIMIT_CAP
): number => Math.max(1, Math.min(Math.floor(pageSize) || 1, cap));

/** Progress label shown during a gradual scan. The exact total is deliberately
 *  dropped (Stefan, 2026-09-25) — computing it is the expensive full-partition
 *  COUNT the gradual read exists to avoid. */
export const scanProgressLabel = (rowCount: number): string =>
    `Showing ${rowCount} so far, scanning older…`;

/** Header line shown above the log list while the forward catch-up (loaded top
 *  row -> newest row) runs on return to the top (Stefan, 2026-10-15). */
export const loadNewerProgressLabel = (rowCount: number): string =>
    `Showing ${rowCount} so far, loading newer…`;

/** Top-bar text while the user reads older logs and newer logs were loaded
 *  above their place (paired with a "Go to newest" button). */
export const newerLogsAboveLabel = (count: number): string =>
    `${count} newer ${count === 1 ? "log" : "logs"} above`;

/** Header line shown when the user has cancelled the forward catch-up: newer logs
 *  may exist above the loaded top row and live updates are paused. Paired with a
 *  "Load newer logs" resume button. */
export const loadNewerCancelledLabel = (rowCount: number): string =>
    `Showing ${rowCount} so far. Loading newer logs was cancelled, so newer logs may be missing and live updates are paused.`;

/** Footer line shown when the user has CANCELLED the older-log scan while older
 *  history still remains (distinct from the terminal "beginning of logs" marker,
 *  which means the oldest log has genuinely been reached). It tells the user that
 *  loading was cancelled and — when known — how far back logs have been loaded so
 *  far, so cancelling a scan is never mistaken for "no older logs exist"
 *  (Margot, 2026-10-14). The boundary date is pre-formatted by the component
 *  (locale/timezone-aware) and passed in; null when unknown. Paired with a
 *  "Scan for older logs" resume affordance. */
export const loadOlderCancelledLabel = (
    rowCount: number,
    scannedThroughLabel: string | null
): string =>
    scannedThroughLabel
        ? `Showing ${rowCount} so far — loading older logs was cancelled (loaded back to ${scannedThroughLabel}).`
        : `Showing ${rowCount} so far — loading older logs was cancelled.`;

/** What the gradual-load footer under the log table should show. Pure so the
 *  component stays thin and the state logic is unit-testable. Priority: a fetch
 *  error is surfaced first, then an in-flight scan (progress + how-far-back +
 *  Cancel), then the "load older" affordance when older history remains, then the
 *  terminal "beginning of logs" marker once the oldest log has been reached, else
 *  nothing (no rows at all).
 *
 *  `scanned_through` is the oldest instant the scan has reached so far; it is
 *  carried on the scanning state so the component can tell the user how far back
 *  it has already scanned (Stefan, 2026-10-01). The raw ISO instant is passed
 *  through (not formatted) so this layer stays locale/timezone-free and the
 *  component owns localized display. */
export type GradualFooterState =
    | { kind: "error" }
    | { kind: "scanning"; label: string; scannedThrough: string | null }
    | { kind: "load-older" }
    | { kind: "end-of-logs" }
    | { kind: "idle" };

/**
 * Whether the Logs tab should automatically fetch the next older screenful right
 * now (scroll-driven, no "Load older logs" button — Stefan, 2026-10-10: "Drop it.
 * When scrolling down, more logs should be loaded early … when the button would
 * be visible it is already scanning"). A bottom sentinel placed below the list
 * (observed with a generous rootMargin so it trips BEFORE the real bottom is
 * reached) drives this; the pure decision is factored out so the re-entrancy
 * guards are unit-testable without React/IntersectionObserver.
 *
 * Applies to ALL Logs-tab views — the single-record tab AND the shared
 * container / workspace logs page (Stefan, 2026-10-13: "it should automatically
 * load older logs when scrolled to the very bottom"). Auto-scanning the
 * container/workspace page (which has no created_at floor) is safe now that each
 * gesture is ONE bounded count-mode fetch: a screenful loads, fills the viewport,
 * and pushes the sentinel out of view, so the walk advances one user-driven step
 * at a time rather than running to the retention horizon. (This supersedes the
 * earlier single-record-only restriction, which predated count-mode paging.)
 *
 * Fire only when ALL hold:
 *   - `enabled` — the live-updates toggle is on (off freezes the view).
 *   - `sentinelVisible` — the user has scrolled the prefetch sentinel into view
 *     (or the loaded content is shorter than the viewport, so the sentinel is
 *     already on screen and the next day loads immediately to fill it).
 *   - `moreAvailable` — older history remains (false at the created_at floor →
 *     terminal "beginning of logs", nothing more to fetch).
 *   - `!isScanning` — re-entrancy guard: a scan is not already in flight. Once a
 *     fetch starts this is true and blocks a second trigger; when it finishes
 *     and the sentinel is still visible with more available, it fires again —
 *     the continuous early-prefetch Stefan asked for.
 *   - `hasRows` — at least one row is loaded. The initial window is kicked off
 *     by the hook itself; the sentinel only ever drives OLDER loads.
 */
export const shouldAutoPrefetchOlder = (params: {
    enabled: boolean;
    sentinelVisible: boolean;
    moreAvailable: boolean;
    isScanning: boolean;
    hasRows: boolean;
}): boolean => {
    const { enabled, sentinelVisible, moreAvailable, isScanning, hasRows } = params;
    return enabled && sentinelVisible && moreAvailable && !isScanning && hasRows;
};

export const gradualFooterState = (params: {
    isScanning: boolean;
    moreAvailable: boolean;
    hasError: boolean;
    rowCount: number;
    scannedThrough?: string | null;
}): GradualFooterState => {
    const { isScanning, moreAvailable, hasError, rowCount, scannedThrough = null } = params;
    if (hasError) {
        return { kind: "error" };
    }
    if (isScanning) {
        return { kind: "scanning", label: scanProgressLabel(rowCount), scannedThrough };
    }
    if (moreAvailable) {
        return { kind: "load-older" };
    }
    // History exhausted and nothing is in flight. Once at least one row has been
    // shown, mark the terminal beginning-of-logs state so the user knows the
    // oldest log has been reached — no log can predate the record's creation (the
    // server floors the gradual scan at the record's created_at). With zero rows
    // there is simply nothing to show, so stay idle.
    if (rowCount > 0) {
        return { kind: "end-of-logs" };
    }
    return { kind: "idle" };
};
